import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
// Steps
import ForgotPasswordStep from "../forgotPasswordStep/ForgotPasswordStep";

import Logo from "@/ui/logo/Logo";
import Button from "@/ui/button/Button";
import LanguageSwitcher from "@/ui/languageSwitcher/LanguageSwitcher";

import style from "./ForgotPasswordLayout.module.css";

import { useFormWithValidation } from "@/features/forms/hooks/useFormWithValidation";
import { FormProvider } from "react-hook-form";
import FormInput from "@/features/forms/components/FormInput";
import InputPassword from "@/features/forms/components/InputPassword";
import ForgotPasswordContext from "../../contexts/ForgotPasswordContext";
import { useSendForgotPasswordCode } from "../../hooks/useSendForgotPasswordCode";
import { useUseForgotPasswordCode } from "../../hooks/useUseForgotPasswordCode";
import { errorToast, warningToast } from "@/ui/toast/Toast";
import i18n from "@/i18n";

function ForgotPasswordLayout({ code }) {
	const { t } = useTranslation();
	const [currentStep, setCurrentStep] = useState(code ? 2 : 0);

	useEffect(() => {
		if (code) {
			setCurrentStep(2);
		}
	}, [code]);

	const steps = [
		{
			title: t("auth:forgot.steps.request.title"),
			description: t("auth:forgot.steps.request.description"),
			icon: "fa-key-skeleton",
			actionName: t("common:actions.continue"),
		},
		{
			title: t("auth:forgot.steps.sent.title"),
			description: t("auth:forgot.steps.sent.description"),
			icon: "fa-envelope",
		},
		{
			title: t("auth:forgot.steps.reset.title"),
			description: t("auth:forgot.steps.reset.description"),
			icon: "fa-lock",
			actionName: t("common:actions.continue"),
		},
	];
	return (
		<ForgotPasswordContext.Provider
			value={{ currentStep, setCurrentStep, code }}
		>
			<div className={style.contentHolder}>
				<div className={style.header}>
					<Logo />
					<LanguageSwitcher />
				</div>
				<div className={style.content}>
					<ForgotPasswordStep
						button={!(currentStep == 3)}
						{...steps[currentStep]}
					>
						{currentStep === 0 && <EmailRequest />}
						{currentStep === 2 && <PasswordReset />}
					</ForgotPasswordStep>
				</div>
			</div>
		</ForgotPasswordContext.Provider>
	);
}

export default ForgotPasswordLayout;

function EmailRequest({ email }) {
	const { t } = useTranslation();
	const { setCurrentStep } = useContext(ForgotPasswordContext);

	const { save } = useSendForgotPasswordCode();

	const onSubmit = async (data) => {
		await save(data);
		setCurrentStep(1);
	};

	const formMethods = useFormWithValidation({
		defaultValues: {
			email: email,
		},
	});

	return (
		<FormProvider {...formMethods}>
			<form onSubmit={formMethods.handleSubmit(onSubmit)}>
				<FormInput
					type="email"
					name="email"
					label={t("common:fields.email.label")}
					placeholder={t("common:fields.email.placeholder")}
					autoComplete="off"
					required={true}
				/>
				<Button
					type="accent"
					className={style.btn}
					disabled={formMethods.formState.isSubmitting}
				>
					{t("common:actions.continue")}
				</Button>
			</form>
		</FormProvider>
	);
}

function PasswordReset() {
	const { t } = useTranslation();
	const { code, setCurrentStep } = useContext(ForgotPasswordContext);
	const { save } = useUseForgotPasswordCode();

	const formMethods = useFormWithValidation();

	const onSubmit = async (data) => {
		try {
			await save({ code, ...data });
			setCurrentStep(3);
		} catch (e) {
			if (e?.response?.data?.errors?.code) {
				// i18n.t, not t: the language may have changed during the request.
				warningToast(i18n.t("auth:forgot.expired"));

				setCurrentStep(0);
			} else {
				throw e;
			}
		}
	};

	return (
		<FormProvider {...formMethods}>
			<form onSubmit={formMethods.handleSubmit(onSubmit)}>
				<InputPassword
					name="new_password1"
					placeholder={t("common:fields.password.placeholder")}
					label={t("auth:forgot.newPassword")}
					required={true}
				/>
				<InputPassword
					name="new_password2"
					placeholder={t("common:fields.password.placeholder")}
					label={t("common:fields.confirmPassword.label")}
					required={true}
				/>
				<Button
					type="accent"
					className={style.btn}
					disabled={formMethods.formState.isSubmitting}
				>
					{t("common:actions.continue")}
				</Button>
			</form>
		</FormProvider>
	);
}
